import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import React from "react";
import type { InputProps } from "../../../types/FormTypes";
import Pressable from "../../common/Pressable/Pressable";
import styles from "./RadioButtons.module.scss";

export interface RadioButtonsOption<TValue extends string | number = string> {
  label: React.ReactNode;
  value: TValue;
  disabled?: boolean;
}

/** @deprecated Use RadioGroup with RadioButton instead. */
export interface RadioButtonsProps<TValue extends string | number = string> extends InputProps<TValue> {
  /** @deprecated Use RadioButton children instead. */
  options: RadioButtonsOption<TValue>[];
  name?: string;
  size?: "sm" | "md";
}

const DefaultRadioButtons = <TValue extends string | number = string>(props: RadioButtonsProps<TValue>) => {
  const { className, value, onValueChange, onChange, options, disabled, name, size = "md" } = props;

  const handleClick = React.useCallback(
    (nextValue: TValue) => {
      if (disabled) return;
      if (nextValue === value) return;
      onValueChange?.(nextValue);
      onChange?.({ target: { value: nextValue } } as React.ChangeEvent<HTMLInputElement>);
    },
    [disabled, onChange, onValueChange, value],
  );

  return (
    <div
      className={clsx(styles.root, className)}
      data-deci-radio-buttons="true"
      data-size={size}
      aria-label={name}
    >
      {options.map((option) => (
        <Pressable
          key={option.value}
          data-focus-ring
          className={clsx(styles.button)}
          data-selected={value === option.value ? "true" : "false"}
          onClick={() => handleClick(option.value)}
          disabled={Boolean(disabled || option.disabled)}
          aria-pressed={value === option.value}
        >
          {option.label}
        </Pressable>
      ))}
    </div>
  );
};

/** @deprecated Use RadioGroup with RadioButton instead. */
const RadioButtons = createPluggable<RadioButtonsProps<any>>("deci.radioButtons", DefaultRadioButtons);

export default RadioButtons;
